import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AuthenticationCard from '../login/components/AuthenticationCard';
import GoogleSignInButton from '../login/components/GoogleSignInButton';
import EmailPasswordForm from '../login/components/EmailPasswordForm';
import TrustSignals from '../login/components/TrustSignals';
import ErrorMessage from '../login/components/ErrorMessage';
import LoadingOverlay from '../login/components/LoadingOverlay';
import FeatureHighlights from '../login/components/FeatureHighlights';

const SignUp = () => {
  const navigate = useNavigate();
  const { signInWithGoogle, isAuthenticated, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [authMode, setAuthMode] = useState('email'); // 'email' or 'google'

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate('/file-browser');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleGoogleSignIn = async () => {
    setError('');
    setIsLoading(true);
    setShowLoadingOverlay(true);

    try {
      const { error } = await signInWithGoogle();
      
      if (error) {
        throw error;
      }

      // OAuth will redirect, no need to manually navigate
    } catch (err) {
      setShowLoadingOverlay(false);
      setIsLoading(false);
      setError(err?.message || 'Unable to authenticate with Google. Please check your internet connection and try again.');
      console.error('Authentication error:', err);
    }
  };

  const handleRetry = () => {
    setError('');
    handleGoogleSignIn();
  };

  // Show loading while checking authentication
  if (authLoading) {
    return <LoadingOverlay isVisible={true} />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <LoadingOverlay isVisible={showLoadingOverlay} />
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <AuthenticationCard>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-foreground mb-2">Create Account</h2>
                  <p className="text-sm text-muted-foreground">
                    Sign up to start managing your S3 files
                  </p>
                </div>

                {/* Auth mode toggle */}
                <div className="flex gap-2 mb-6">
                  <button
                    type="button"
                    onClick={() => setAuthMode('email')}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-colors ${
                      authMode === 'email' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    Email / Password
                  </button>
                  <button
                    type="button"
                    onClick={() => setAuthMode('google')}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-colors ${
                      authMode === 'google' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    Google
                  </button>
                </div>

                {authMode === 'google' ? (
                  <>
                    <GoogleSignInButton
                      onSignIn={handleGoogleSignIn}
                      isLoading={isLoading}
                    />
                    
                    <ErrorMessage
                      message={error}
                      onRetry={handleRetry}
                    />
                  </>
                ) : (
                  <>
                    <EmailPasswordForm mode="signup" />
                    
                    <div className="mt-4 text-center">
                      <p className="text-sm text-muted-foreground">
                        Already have an account?{' '}
                        <button
                          type="button"
                          onClick={() => navigate('/login')}
                          className="text-primary font-medium hover:underline"
                        >
                          Sign in
                        </button>
                      </p>
                    </div>
                  </>
                )}

                <TrustSignals />
              </AuthenticationCard>
            </div>

            <div className="order-1 lg:order-2 hidden lg:block">
              <div className="bg-card rounded-2xl shadow-lg border border-border p-8">
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-foreground mb-4">
                    Join S3 File Manager
                  </h2>
                  <p className="text-base text-muted-foreground leading-relaxed">
                    Start managing your S3 buckets with ease. Upload, organize, and share files securely with enterprise-grade authentication.
                  </p>
                </div>

                <FeatureHighlights />

                <div className="mt-8 pt-8 border-t border-border">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-success"></div>
                      <span>Free to start</span>
                    </div>
                    <div className="w-px h-4 bg-border"></div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-primary"></div>
                      <span>No credit card</span>
                    </div>
                    <div className="w-px h-4 bg-border"></div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-warning"></div>
                      <span>Cancel anytime</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-xs text-muted-foreground">
              By signing up, you agree to our Terms of Service and Privacy Policy
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              &copy; {new Date()?.getFullYear()} S3 File Manager. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;